/**
 * ResponsiveImage — drop-in <img> replacement that:
 *
 *   1. Lazy-loads off-screen images (`loading="lazy"`).
 *   2. Decodes async so the carousel scroll doesn't jank.
 *   3. Emits a `srcSet` of 400w / 800w / 1200w variants when the source
 *      URL is hosted on our Cloudflare R2 CDN AND Cloudflare Image
 *      Transformations are enabled on the zone. Until that's toggled in
 *      the CF dashboard, the wrapper transparently falls back to the
 *      original URL — no broken images, no extra requests.
 *   4. Soft-fails to a black placeholder if the source 404s, instead of
 *      showing the browser's default broken-image icon.
 *
 * Usage is identical to <img>:
 *     <ResponsiveImage src={card.image_url} alt={card.name} />
 *     <ResponsiveImage src={url} alt="…" sizes="(max-width: 640px) 100vw, 33vw" />
 *
 * Future flip: once Cloudflare Image Resizing is enabled on the zone,
 * the constant `CF_IMAGE_TRANSFORMS_ENABLED` below goes to `true` and
 * the srcSet starts hitting `/cdn-cgi/image/width=...` variants —
 * automatic ~60% mobile data win across every adopting surface.
 */
import React, { useState } from "react";

// Cloudflare Image Transformations enabled on the `media.blkpacks.fun`
// zone as of 2026-02-23 (FREE plan, 5,000 unique transforms/mo limit).
// Verified: 400w transform of a 223 KB PNG returned 15 KB (93% smaller).
const CF_IMAGE_TRANSFORMS_ENABLED = true;

// Default responsive widths. Tuned for our card grid: phones land on 400w,
// 2-column tablets on 800w, full desktop on 1200w.
const DEFAULT_WIDTHS = [400, 800, 1200];

// Hosts where we know on-the-fly resizing is supported. Anything else
// (e.g. customer-assets.emergentagent.com, data URIs, Pollinations) is
// emitted as a plain <img> with lazy + decoding only.
const RESIZABLE_HOSTS = ["media.blkpacks.fun", "blkpacks.fun"];

const buildCfTransformUrl = (origUrl, width) => {
  // CF Image Resizing URL format:
  //   https://<zone>/cdn-cgi/image/width=400,format=auto,quality=80/<path>
  // We have to rewrite the URL by injecting the transform prefix right
  // after the host. Keep the original origin so cookies / CDN cache
  // behaviour stays identical.
  try {
    const u = new URL(origUrl);
    const path = u.pathname.replace(/^\/+/, "");
    return `${u.origin}/cdn-cgi/image/width=${width},format=auto,quality=82/${path}`;
  } catch {
    return origUrl;
  }
};

const isResizable = (url) => {
  if (!url || typeof url !== "string") return false;
  if (url.startsWith("data:") || url.startsWith("blob:")) return false;
  try {
    const host = new URL(url).hostname;
    return RESIZABLE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
};

const ResponsiveImage = ({
  src,
  alt = "",
  className,
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  loading = "lazy",
  decoding = "async",
  fetchPriority,
  widths = DEFAULT_WIDTHS,
  onError,
  ...rest
}) => {
  const [failed, setFailed] = useState(false);

  // canResize must be computed BEFORE the early-return so hook order
  // stays stable across renders (rules-of-hooks).
  const canResize = CF_IMAGE_TRANSFORMS_ENABLED && isResizable(src);

  // Usage telemetry — sampled 1 in 5 transforms so we don't add traffic.
  // Fire-and-forget. Skips when not actually transforming (no zone hit).
  // MUST live above the early-return for hooks rules.
  React.useEffect(() => {
    if (!canResize) return;
    if (Math.random() > 0.2) return; // 20% sample
    try {
      const beaconUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/cf-images/ping`;
      const data = JSON.stringify({ url: src, width: widths[0] });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(beaconUrl, new Blob([data], { type: "application/json" }));
      } else {
        fetch(beaconUrl, { method: "POST", body: data, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
      }
    } catch { /* never break the image render over telemetry */ }
  }, [canResize, src, widths]);

  if (!src || failed) {
    return (
      <div
        className={`${className || ""} bg-black/60 flex items-center justify-center text-white/20 text-[10px] tracking-widest uppercase`}
        role={alt ? "img" : undefined}
        aria-label={alt || undefined}
        data-testid="responsive-image-fallback"
      >
        {failed ? "Image unavailable" : ""}
      </div>
    );
  }

  const srcSet = canResize
    ? widths.map((w) => `${buildCfTransformUrl(src, w)} ${w}w`).join(", ")
    : undefined;
  const finalSrc = canResize ? buildCfTransformUrl(src, widths[widths.length - 1]) : src;

  return (
    <img
      src={finalSrc}
      srcSet={srcSet}
      sizes={canResize ? sizes : undefined}
      alt={alt}
      className={className}
      loading={loading}
      decoding={decoding}
      fetchPriority={fetchPriority}
      onError={(e) => {
        setFailed(true);
        if (onError) onError(e);
      }}
      {...rest}
    />
  );
};

export default ResponsiveImage;
